import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const PendingVerifiedLoans = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [loans, setLoans] = useState([]);
  const [openDetails, setOpenDetails] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [editingLoan, setEditingLoan] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [selectedLoanSchedule, setSelectedLoanSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nicFilter, setNicFilter] = useState('');

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/officer/loans', {
        params: { officerId: user.userId },
      });
      // Filter only PENDING and VERIFIED loans (can edit until approved)
      const pendingVerifiedLoans = (response.data || []).filter(
        loan => loan.status === 'PENDING' || loan.status === 'VERIFIED'
      );
      // Sort by created date (newest first)
      const sortedLoans = pendingVerifiedLoans.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
      setLoans(sortedLoans);
    } catch (error) {
      console.error('Error fetching pending/verified loans:', error);
      toast.error('Error fetching loans');
    } finally {
      setLoading(false);
    }
  };

  const openDetailsDialog = async (loan) => {
    setSelectedLoan(loan);
    setSelectedLoanSchedule(null);
    setLoadingSchedule(true);
    setOpenDetails(true);
    
    try {
      const response = await api.get(`/officer/loans/${loan.id}/schedule`);
      setSelectedLoanSchedule(response.data);
    } catch (error) {
      console.error('Error fetching loan schedule:', error);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleOpenEditDialog = (loan) => {
    setEditingLoan(loan);
    setEditAmount(loan.requestedAmount?.toString() || '');
    setOpenEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingLoan || !editAmount) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amount = parseFloat(editAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }

    try {
      setSaving(true);
      // Check if loan is still editable (not approved yet)
      if (editingLoan.status === 'APPROVED' || editingLoan.status === 'ACTIVE') {
        toast.error('Cannot edit loan amount after approval');
        setOpenEditDialog(false);
        fetchLoans();
        return;
      }

      await api.put(`/officer/loans/${editingLoan.id}/amount`, {}, {
        params: { requestedAmount: amount }
      });
      
      toast.success('Loan amount updated successfully');
      setOpenEditDialog(false);
      setEditingLoan(null);
      setEditAmount('');
      fetchLoans();
    } catch (error) {
      console.error('Error updating loan amount:', error);
      toast.error(error.response?.data?.error || 'Error updating loan amount');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setOpenEditDialog(false);
    setEditingLoan(null);
    setEditAmount('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'VERIFIED':
        return 'info';
      default:
        return 'default';
    }
  };

  // Filter loans by NIC
  const filteredLoans = loans.filter((loan) => {
    if (!nicFilter.trim()) return true;
    const customer = loan.customer;
    const nic = customer?.nic || '';
    return nic.toLowerCase().includes(nicFilter.toLowerCase().trim());
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/officer/dashboard')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: '#0D47A1',
              fontSize: { xs: '1.75rem', sm: '2rem' },
            }}
          >
            Pending / Verified Loans
          </Typography>
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        You can edit the loan amount for PENDING and VERIFIED loans until they are approved. Once approved, the amount cannot be changed.
      </Alert>

      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">Pending Loans</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.main' }}>
                {loans.filter(l => l.status === 'PENDING').length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">Verified Loans</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'info.main' }}>
                {loans.filter(l => l.status === 'VERIFIED').length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">Total Amount</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Rs {loans.reduce((sum, loan) => sum + (parseFloat(loan.requestedAmount || 0)), 0).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filter */}
      <TextField
        fullWidth
        placeholder="Search by Customer NIC..."
        value={nicFilter}
        onChange={(e) => setNicFilter(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Loans Table */}
      {filteredLoans.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No pending or verified loans found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Loan ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>NIC</TableCell>
                <TableCell>Requested Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLoans.map((loan) => (
                <TableRow 
                  key={loan.id}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => openDetailsDialog(loan)}
                >
                  <TableCell>{loan.id}</TableCell>
                  <TableCell>{loan.customer?.fullName || loan.customer?.name || 'N/A'}</TableCell>
                  <TableCell>{loan.customer?.nic || 'N/A'}</TableCell>
                  <TableCell>Rs {loan.requestedAmount?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>
                    <Chip
                      label={loan.status}
                      color={getStatusColor(loan.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {loan.createdAt
                      ? new Date(loan.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditDialog(loan)}
                        color="primary"
                      >
                        Edit Amount
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => openDetailsDialog(loan)}
                      >
                        View
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Loan Amount - Loan ID: {editingLoan?.id}
        </DialogTitle>
        <DialogContent>
          {editingLoan && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You can edit the loan amount only for PENDING and VERIFIED loans. Once the loan is approved, the amount cannot be changed.
              </Alert>
              <TextField
                fullWidth
                label="Customer Name"
                value={editingLoan.customer?.fullName || editingLoan.customer?.name || 'N/A'}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Current Requested Amount"
                value={`Rs ${editingLoan.requestedAmount?.toFixed(2) || '0.00'}`}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="New Requested Amount"
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Enter the new loan amount"
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={saving} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained" 
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog 
        open={openDetails} 
        onClose={() => setOpenDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Loan Details - ID: {selectedLoan?.id}
        </DialogTitle>
        <DialogContent>
          {selectedLoan && (
            <Box>
              {/* Loan Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Loan Information</Typography>
                  <Typography><strong>Loan ID:</strong> {selectedLoan.id}</Typography>
                  <Typography><strong>Customer:</strong> {selectedLoan.customer?.fullName || selectedLoan.customer?.name}</Typography>
                  <Typography><strong>NIC:</strong> {selectedLoan.customer?.nic}</Typography>
                  <Typography><strong>Phone:</strong> {selectedLoan.customer?.phoneNumber}</Typography>
                  <Typography><strong>Requested Amount:</strong> Rs {selectedLoan.requestedAmount?.toFixed(2)}</Typography>
                  <Typography><strong>Status:</strong> 
                    <Chip label={selectedLoan.status} color={getStatusColor(selectedLoan.status)} size="small" sx={{ ml: 1 }} />
                  </Typography>
                  <Typography><strong>Created:</strong> {selectedLoan.createdAt ? new Date(selectedLoan.createdAt).toLocaleString() : 'N/A'}</Typography>
                  {selectedLoan.verificationComments && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom><strong>Verification Comments:</strong></Typography>
                      <Typography variant="body2">{selectedLoan.verificationComments}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Repayment Schedule (if available) */}
              {loadingSchedule ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : selectedLoanSchedule && selectedLoanSchedule.installments && selectedLoanSchedule.installments.length > 0 ? (
                <>
                  <Typography variant="h6" gutterBottom>Repayment Schedule</Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedLoanSchedule.installments
                          ?.filter(inst => inst.installmentNumber > 1)
                          .map((installment) => (
                            <TableRow key={installment.id}>
                              <TableCell>{installment.installmentNumber}</TableCell>
                              <TableCell>
                                {installment.dueDate ? new Date(installment.dueDate).toLocaleDateString() : 'N/A'}
                              </TableCell>
                              <TableCell>Rs {installment.amount?.toFixed(2) || '0.00'}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={installment.status} 
                                  size="small"
                                  color={
                                    installment.status === 'PAID' ? 'success' :
                                    installment.status === 'PARTIAL' ? 'warning' :
                                    'default'
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Repayment schedule will be available after loan approval.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
          {selectedLoan && (selectedLoan.status === 'PENDING' || selectedLoan.status === 'VERIFIED') && (
            <Button 
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setOpenDetails(false);
                handleOpenEditDialog(selectedLoan);
              }}
            >
              Edit Amount
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingVerifiedLoans;
